import React, { useState } from 'react';
import Icon from '@material-ui/core/Icon';
import LogoHorizontal from '../components/LogoHorizontal';
import '../css/landing-page.css';

function Stars({ count = 5, className = '' }) {
    return (
        <span className={`lp-stars ${className}`}>
            {Array.from({ length: count }).map((_, i) => (
                <Icon key={i} className="lp-star">
                    star
                </Icon>
            ))}
        </span>
    );
}

const APPOINTMENT_URL = 'https://tidycal.com/yarden-garden/appointment';
const CLASS_URL = 'https://learnwithyarden.com/coaching';

const FAQS = [
    {
        q: 'What happens during the free quote visit?',
        a: 'A certified Yarden gardener visits your property, assesses your space, soil, and goals, then designs a customized garden plan with transparent pricing — no obligation to commit.',
    },
    {
        q: 'How often does a gardener come out for maintenance?',
        a: "We offer weekly maintenance plans tailored to your garden's needs and your budget. Your gardener will recommend the best cadence after the quote visit.",
    },
    {
        q: 'How does the garden coaching work?',
        a: 'A highly experienced gardening instructor will come to your residence and give you guidence on setting up your garden, and how to keep it growing strong.',
    },
    {
        q: 'Do I need any experience to book an installation?',
        a: 'Not at all. Our team handles everything from design to soil prep, raised bed construction, planting, and ongoing care. You just harvest and enjoy.',
    },
    {
        q: 'What areas do you serve?',
        a: 'We currently serve SF Bay Area. Enter your zip code during booking to confirm availability in your neighborhood.',
    },
];

const GALLERY_IMAGES = [
    {
        src: 'https://yarden-garden.s3.us-west-1.amazonaws.com/gallery-page-images/gallery-19.jpeg',
        alt: 'Lush raised bed garden installed by Yarden',
    },
    {
        src: 'https://yarden-garden.s3.us-west-1.amazonaws.com/gallery-page-images/gallery-4.jpeg',
        alt: 'Fresh organic harvest from a Yarden garden',
    },
    {
        src: 'https://yarden-garden.s3.us-west-1.amazonaws.com/gallery-page-images/gallery-42.jpeg',
        alt: 'Beautiful backyard garden beds with vegetables',
    },
    {
        src: 'https://yarden-garden.s3.us-west-1.amazonaws.com/gallery-page-images/gallery-6.jpeg',
        alt: 'Colorful organic produce freshly harvested',
    },
    {
        src: 'https://yarden-garden.s3.us-west-1.amazonaws.com/gallery-page-images/gallery-23.jpeg',
        alt: 'Professional garden installation by Yarden',
    },
    {
        src: 'https://yarden-garden.s3.us-west-1.amazonaws.com/gallery-page-images/gallery-48.jpeg',
        alt: 'Organic vegetables growing in raised beds',
    },
];

const TESTIMONIALS = [
    {
        name: 'Anne M.',
        location: 'Oakland, CA',
        avatar: 'https://yarden-garden.s3.us-west-1.amazonaws.com/static+assets/anne-marie.png',
        text: '"Yarden turned my neglected backyard into an incredible vegetable garden. The team is professional, knowledgeable, and genuinely passionate about what they do."',
    },
    {
        name: 'Cindy T.',
        location: 'Lafayette, CA',
        avatar: 'https://yarden-garden.s3.us-west-1.amazonaws.com/static+assets/cindy.jpg',
        text: '"I took the 1:1 garden coaching and was growing tomatoes within a month. Couldn\'t believe how much I learned in one session. Best decision ever!"',
    },
    {
        name: 'Ron H.',
        location: 'San Francisco, CA',
        avatar: 'https://yarden-garden.s3.us-west-1.amazonaws.com/static+assets/ron.png',
        text: '"The free quote visit was zero pressure. They listened to exactly what I wanted and designed a garden that fits my life. Couldn\'t recommend more highly."',
    },
];

function LandingPage() {
    const [openFaq, setOpenFaq] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);

    return (
        <div className="lp">
            {/* ── STICKY NAV ─────────────────────────────────────────────────────── */}
            <nav className="lp-nav">
                <LogoHorizontal height={30} />

                {/* Desktop CTAs */}
                <div className="lp-nav-ctas lp-nav-desktop">
                    <a href="/login" className="lp-nav-login">
                        Log In
                    </a>
                    <a
                        href={CLASS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lp-btn lp-btn-outline lp-btn-sm"
                    >
                        Teach Me To Grow
                    </a>
                    <a
                        href={APPOINTMENT_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lp-btn lp-btn-cta lp-btn-sm"
                    >
                        Grow It For Me
                    </a>
                </div>

                {/* Mobile hamburger */}
                <button
                    className="lp-hamburger"
                    onClick={() => setMenuOpen(true)}
                    aria-label="Open menu"
                >
                    <Icon>menu</Icon>
                </button>
            </nav>

            {/* ── MOBILE SLIDE-OUT MENU ───────────────────────────────────────────── */}
            {menuOpen && (
                <div
                    className="lp-mobile-overlay"
                    onClick={() => setMenuOpen(false)}
                />
            )}
            <div
                className={`lp-mobile-menu ${
                    menuOpen ? 'lp-mobile-menu-open' : ''
                }`}
            >
                <button
                    className="lp-mobile-close"
                    onClick={() => setMenuOpen(false)}
                    aria-label="Close menu"
                >
                    <Icon>close</Icon>
                </button>
                <nav className="lp-mobile-nav">
                    <a
                        href={APPOINTMENT_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lp-mobile-link"
                        onClick={() => setMenuOpen(false)}
                    >
                        <Icon className="lp-mobile-link-icon">
                            calendar_today
                        </Icon>
                        Grow It For Me
                    </a>
                    <a
                        href={CLASS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lp-mobile-link"
                        onClick={() => setMenuOpen(false)}
                    >
                        <Icon className="lp-mobile-link-icon">menu_book</Icon>
                        Teach Me To Grow
                    </a>
                    <a
                        href="/login"
                        className="lp-mobile-link"
                        onClick={() => setMenuOpen(false)}
                    >
                        <Icon className="lp-mobile-link-icon">login</Icon>
                        Log In
                    </a>
                </nav>
            </div>

            {/* ── HERO ────────────────────────────────────────────────────────────── */}
            <div className="lp-hero">
                <div className="lp-hero-overlay" />
                <div className="lp-hero-content">
                    <span className="lp-eyebrow">
                        Home Gardening · Your Way
                    </span>
                    <h1 className="lp-hero-headline">
                        Are you ready to grow your own food?
                    </h1>
                    <p className="lp-hero-sub">
                        Whether you want a thriving garden without lifting a
                        finger — or the know-how to grow your own food from
                        scratch — Yarden has a path for you.
                    </p>
                    <div className="lp-hero-buttons">
                        <a
                            href={APPOINTMENT_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="lp-btn lp-btn-cta lp-btn-lg"
                        >
                            Grow It For Me →
                        </a>
                        <a
                            href={CLASS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="lp-btn lp-btn-ghost lp-btn-lg"
                        >
                            Teach Me To Grow
                        </a>
                    </div>
                    <div className="lp-hero-trust">
                        <Stars />
                        <span>Trusted by 500+ homeowners</span>
                    </div>
                </div>
            </div>

            {/* ── TWO OFFERS ──────────────────────────────────────────────────────── */}
            <div className="lp-band lp-band-light">
                <div className="lp-inner">
                    <div className="lp-sh">
                        <h2>Two Ways to Transform Your Garden</h2>
                        <p>
                            Pick the path that fits your lifestyle — or do both.
                        </p>
                    </div>
                    <div className="lp-offers-grid">
                        {/* Offer 1 */}
                        <div className="lp-offer-card lp-offer-card-service">
                            <span className="lp-badge lp-badge-popular">
                                We Grow
                            </span>
                            <Icon className="lp-offer-icon">yard</Icon>
                            <h3>Installation &amp; Maintenance</h3>
                            <p className="lp-offer-tagline">
                                A Yarden gardener comes to you each week. We
                                design, plant, and maintain everything.
                            </p>
                            <ul className="lp-offer-features">
                                <li>
                                    <Icon className="lp-check">
                                        check_circle
                                    </Icon>{' '}
                                    Custom garden design for your property
                                </li>
                                <li>
                                    <Icon className="lp-check">
                                        check_circle
                                    </Icon>{' '}
                                    Professional installation by certified
                                    gardeners
                                </li>
                                <li>
                                    <Icon className="lp-check">
                                        check_circle
                                    </Icon>{' '}
                                    Weekly maintenance visits
                                </li>
                                <li>
                                    <Icon className="lp-check">
                                        check_circle
                                    </Icon>{' '}
                                    Seasonal planting &amp; harvest support
                                </li>
                                <li>
                                    <Icon className="lp-check">
                                        check_circle
                                    </Icon>{' '}
                                    100% organic, pesticide-free methods
                                </li>
                            </ul>
                            <p className="lp-offer-note">
                                Starts with a free on-site quote — no
                                obligation.
                            </p>
                            <a
                                href={APPOINTMENT_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="lp-btn lp-btn-cta lp-btn-full"
                            >
                                Get FREE Quote →
                            </a>
                        </div>

                        {/* Offer 2 */}
                        <div className="lp-offer-card lp-offer-card-class">
                            <span className="lp-badge lp-badge-popular">
                                You Grow
                            </span>
                            <Icon className="lp-offer-icon">menu_book</Icon>
                            <h3>1-on-1 Garden Coaching</h3>
                            <p className="lp-offer-tagline">
                                Learn to grow your own food from scratch.
                                Beginner-friendly and taught by experts.
                            </p>
                            <ul className="lp-offer-features">
                                <li>
                                    <Icon className="lp-check">
                                        check_circle
                                    </Icon>{' '}
                                    No experience necessary
                                </li>
                                <li>
                                    <Icon className="lp-check">
                                        check_circle
                                    </Icon>{' '}
                                    Soil prep, planting &amp; watering basics
                                </li>
                                <li>
                                    <Icon className="lp-check">
                                        check_circle
                                    </Icon>{' '}
                                    What to grow by season in your climate
                                </li>
                                <li>
                                    <Icon className="lp-check">
                                        check_circle
                                    </Icon>{' '}
                                    Tools, tips &amp; common mistakes to avoid
                                </li>
                                <li>
                                    <Icon className="lp-check">
                                        check_circle
                                    </Icon>{' '}
                                    Live Q&amp;A with Yarden experts
                                </li>
                            </ul>
                            <p className="lp-offer-note">
                                Register in under 60 seconds. Spots fill up
                                quickly.
                            </p>
                            <a
                                href={CLASS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="lp-btn lp-btn-cta lp-btn-full"
                            >
                                Book Session →
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── TRUST STRIP ─────────────────────────────────────────────────────── */}
            <div className="lp-band lp-band-forest">
                <div className="lp-inner-flush">
                    <div className="lp-trust-strip">
                        <div className="lp-trust-item">
                            <Icon className="lp-trust-icon">home</Icon>
                            <strong>500+</strong>
                            <small>Gardens installed</small>
                        </div>
                        <div className="lp-trust-item">
                            <Icon className="lp-trust-icon">star</Icon>
                            <strong>4.9 / 5</strong>
                            <small>Average rating</small>
                        </div>
                        <div className="lp-trust-item">
                            <Icon className="lp-trust-icon">eco</Icon>
                            <strong>100%</strong>
                            <small>Organic methods</small>
                        </div>
                        <div className="lp-trust-item">
                            <Icon className="lp-trust-icon">school</Icon>
                            <strong>2,000+</strong>
                            <small>Students enrolled</small>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── HOW IT WORKS ────────────────────────────────────────────────────── */}
            <div className="lp-band lp-band-white">
                <div className="lp-inner">
                    <div className="lp-sh">
                        <h2>How It Works</h2>
                        <p>Getting started takes less than 5 minutes.</p>
                    </div>
                    <div className="lp-hiw-steps">
                        <div className="lp-hiw-step">
                            <div className="lp-hiw-num">1</div>
                            <h4>Book Your Free Visit</h4>
                            <p>
                                Pick a time that works for you. A certified
                                Yarden gardener comes to assess your space and
                                goals.
                            </p>
                        </div>
                        <div className="lp-hiw-step">
                            <div className="lp-hiw-num">2</div>
                            <h4>Get Your Custom Plan</h4>
                            <p>
                                We design a garden tailored to your property and
                                budget, with transparent pricing and no hidden
                                fees.
                            </p>
                        </div>
                        <div className="lp-hiw-step">
                            <div className="lp-hiw-num">3</div>
                            <h4>Watch It Grow</h4>
                            <p>
                                Our team installs and maintains your garden. You
                                harvest fresh produce and enjoy the results.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── GALLERY ─────────────────────────────────────────────────────────── */}
            <div className="lp-band lp-band-light">
                <div className="lp-inner">
                    <div className="lp-sh">
                        <h2>Real Results from Real Homeowners</h2>
                        <p>
                            A look at the gardens we've built and the harvests
                            we've grown.
                        </p>
                    </div>
                    <div className="lp-gallery-grid">
                        {GALLERY_IMAGES.map((img, i) => (
                            <div key={i} className="lp-gallery-item">
                                <img src={img.src} alt={img.alt} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── TESTIMONIALS ────────────────────────────────────────────────────── */}
            <div className="lp-band lp-band-sage">
                <div className="lp-inner">
                    <div className="lp-sh">
                        <h2>What Our Customers Are Saying</h2>
                    </div>
                    <div className="lp-testimonials-grid">
                        {TESTIMONIALS.map((t, i) => (
                            <div key={i} className="lp-testimonial-card">
                                <Stars className="lp-testimonial-stars" />
                                <p className="lp-testimonial-text">{t.text}</p>
                                <div className="lp-testimonial-author">
                                    <img
                                        src={t.avatar}
                                        alt={t.name}
                                        className="lp-testimonial-avatar"
                                    />
                                    <div>
                                        <strong>{t.name}</strong>
                                        <small>{t.location}</small>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── VIDEO ───────────────────────────────────────────────────────────── */}
            <div className="lp-band lp-band-white">
                <div className="lp-inner">
                    <div className="lp-sh">
                        <h2>See Yarden in Action</h2>
                        <p>
                            Watch how we transform a backyard in under a week.
                        </p>
                    </div>
                    <div className="lp-video-wrapper">
                        <iframe
                            src="https://www.youtube.com/embed/APVQvFnEdgY"
                            title="See Yarden in Action"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                </div>
            </div>

            {/* ── FAQ ─────────────────────────────────────────────────────────────── */}
            <div className="lp-band lp-band-light">
                <div className="lp-inner-narrow">
                    <div className="lp-sh">
                        <h2>Frequently Asked Questions</h2>
                    </div>
                    <div className="lp-faq-list">
                        {FAQS.map((faq, i) => (
                            <div key={i} className="lp-faq-item">
                                <button
                                    className="lp-faq-q"
                                    onClick={() =>
                                        setOpenFaq(openFaq === i ? null : i)
                                    }
                                >
                                    <span>{faq.q}</span>
                                    <span className="lp-faq-icon">
                                        <Icon>
                                            {openFaq === i ? 'remove' : 'add'}
                                        </Icon>
                                    </span>
                                </button>
                                {openFaq === i && (
                                    <p className="lp-faq-a">{faq.a}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── FINAL CTA ───────────────────────────────────────────────────────── */}
            <div className="lp-band lp-band-dark">
                <div className="lp-inner-narrow">
                    <div className="lp-final-cta">
                        <h2>We serve the San Francisco Bay Area.</h2>
                        <p>
                            Join hundreds of homeowners who've turned their
                            yards into thriving, productive gardens.
                        </p>
                        <div className="lp-final-cta-btns">
                            <a
                                href={APPOINTMENT_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="lp-btn lp-btn-cta lp-btn-lg"
                            >
                                Grow It For Me →
                            </a>
                            <a
                                href={CLASS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="lp-btn lp-btn-ghost lp-btn-lg"
                            >
                                Teach Me To Grow
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── FOOTER ──────────────────────────────────────────────────────────── */}
            <div
                className="lp-band lp-band-white"
                style={{ borderTop: '1px solid #e5e7eb' }}
            >
                <footer className="lp-footer">
                    <LogoHorizontal height={26} />
                    <p className="lp-footer-tagline">
                        Master Your Gardening Skills or Let Us Do the Work.
                    </p>
                    <div className="lp-footer-links">
                        <a href="/privacy-policy">Privacy Policy</a>
                        <a href="/terms-of-service">Terms of Service</a>
                        <a
                            href={APPOINTMENT_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Grow It For Me
                        </a>
                        <a
                            href={CLASS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Teach Me To Grow
                        </a>
                    </div>
                    <p className="lp-footer-copy">
                        © {new Date().getFullYear()} Yarden. All rights
                        reserved.
                    </p>
                </footer>
            </div>
        </div>
    );
}

export default LandingPage;
